import React, { CSSProperties, ReactNode, useState, useEffect } from "react";
import { Button, Divider, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import {
  MATTER_EDITOR_ASSUMPTIONS,
  MATTER_EDITOR_DISBURSEMENTS,
  MATTER_EDITOR_DISCOUNTS,
  MATTER_EDITOR_EXCLUSIONS,
  MATTER_EDITOR_SCOPE,
  MATTER_EDITOR_UPLIFTS,
  MATTER_EDITOR_FEE_RISK_PROFILE,
} from "../matter-editor";
import {
  // AssignedResource,
  isNumber,
  isPrice,
  isPriceDiscount,
  isPriceRangeDiscount,
  isTax,
  PercentageRange,
  Price,
  PriceRange,
  PricingType,
  Proposal,
  LegalFeeLineItemTask,
  LegalFeeLineItem,
  hasDiscount,
  LegalFeeDiscount,
  PricingLevel,
  // FeeRiskProfileDiagramProps,
  NumberedListFormat,
} from "../../data-types";
const { Text, Paragraph } = Typography;
import styles from "./modal.css";
import { TaxLabel } from "~/gql/graphql";
// import { FeeRiskProfileDiagram } from "../fee-risk-profile";

import {
  ProposalContentBuilder,
  JSXContentGenerator,
  textContent,
} from "@virtual-pricing-director/vpd-proposal";
import { renderFeeRiskProfileDiagram } from "@virtual-pricing-director/vpd-illustration";

const PRE_LINE_STYLE: CSSProperties = {
  whiteSpace: "pre-line",
};

const {
  // ["non-styled-list"]: nonStyledListClassName,
  // ["lower-alpha-list"]: lowerAlphaListClassName,
  ["label-with-edit-link"]: labelWithEditLink,
  ["table-row"]: tableRow,
  ["table-first-cell"]: tableFirstCell,
  ["table-other-cell"]: tableOtherCell,
  ["proposal-line-items"]: proposalLineItemsClassName,
  ["header-logo"]: headerLogo,
  // ["bracketsList"]: bracketsListClassName,
  // ["center-container"]: centerContainerClassName,
  // ["fee-risk-table"]: feeRiskTableClassName,
} = styles as any;

const EditButton = ({ onClick }: { onClick: () => void }) => (
  <Button icon={<EditOutlined />} type="link" onClick={onClick} />
);
type LabelWithEditLinkProps = {
  text: string;
  onClick: () => void;
};
const LabelWithEditLink = ({ text, onClick }: LabelWithEditLinkProps) => (
  <div className={labelWithEditLink}>
    {text}
    <EditButton onClick={onClick} />
  </div>
);
const isLabelWithEditLinkProps = (value: any): value is LabelWithEditLinkProps =>
  value["text"] && value["onClick"];
type RowProps = {
  label: ReactNode | { text: ReactNode; onClickEdit: (tabKey: string) => void };
  values?: (string | JSX.Element)[];
  strong?: "label" | "row";
  style?: React.CSSProperties;
};
export const Row = ({ label, values = [], strong, style }: RowProps) => (
  <tr className={tableRow} style={style}>
    <td className={tableFirstCell}>
      {isLabelWithEditLinkProps(label) ? (
        <LabelWithEditLink {...label} />
      ) : (
        <Text strong={strong === "label" || strong === "row"}>{label}</Text>
      )}
    </td>
    {values.map((v, i) => {
      return (
        <td className={tableOtherCell} key={i}>
          <Text strong={strong === "row"}>{v}</Text>
        </td>
      );
    })}
  </tr>
);
export const SpacerRow = () => (
  <tr>
    <td colSpan={2}>{"\u00A0"}</td>
  </tr>
);
export const DividerRow = ({ colSpan = 2 }: { colSpan?: number }) => (
  <tr>
    <td colSpan={colSpan}>
      <Divider />
    </td>
  </tr>
);

const LabelByPricingType: Record<PricingType, string> = {
  [PricingType.Estimated]: "estimated fee",
  [PricingType.EstimatedRange]: "estimated fee range",
  [PricingType.Capped]: "capped fee",
  [PricingType.Fixed]: "fixed fee",
  [PricingType.TopDown]: "top-down fee",
  [PricingType.None]: "(pricing type not selected)",
};

const formatPrice = (value: number | Price | PriceRange, currencySymbol: string) => {
  if (isNumber(value)) {
    const amount = Math.round(value).toLocaleString();
    return `${currencySymbol}${amount}`;
  } else if (isPrice(value)) {
    const amount = Math.round(value.amount).toLocaleString();
    return `${currencySymbol}${amount}`;
  } else {
    const lower = Math.round(value.lower || 0).toLocaleString();
    const upper = Math.round(value.upper || 0).toLocaleString();
    return `${currencySymbol}${lower} to ${currencySymbol}${upper}`;
  }
};

export const formatPercentage = (value: number | PercentageRange) => {
  if (isNumber(value)) {
    const percentage = Math.round(value);
    return `${percentage}%`;
  } else {
    const lower = Math.round(value.lower);
    const upper = Math.round(value.upper);
    return lower === upper ? `${lower}%` : `${lower}% to ${upper}%`;
  }
};

const HeaderLogo = ({
  src,
  width = "auto",
  height = "60",
  className,
}: {
  src: string;
  width?: string;
  height?: string;
  className?: string;
}): JSX.Element => {
  return <img className={className} width={width} height={height} src={src} />;
};

const Footer = ({
  src,
  width = "100%",
  height = "auto",
  className,
}: {
  src: string;
  width?: string;
  height?: string;
  className?: string;
}): JSX.Element => {
  return <img className={className} width={width} height={height} src={src} />;
};

const HeaderLogoSection = ({ logo }: { logo: string }) => {
  return (
    <Paragraph>
      <HeaderLogo className={headerLogo} src={logo} />
    </Paragraph>
  );
};

const FooterSection = ({ footer }: { footer: string }) => {
  return (
    <Paragraph style={{ marginTop: "30px" }}>
      <Footer className={headerLogo} src={footer} />
    </Paragraph>
  );
};

// const PricingTypeAndIntroSection = ({
//   pricingType,
//   hasFixed,
//   numberedListFormat,
// }: {
//   pricingType: PricingType;
//   hasFixed: boolean;
//   numberedListFormat: NumberedListFormat;
// }) => {
//   return (
//     <>
//       <Paragraph>
//         We are pleased to provide you with the following pricing proposal. Please note that:
//       </Paragraph>
//       <Paragraph>
//         <ol className={classNames({ [bracketsListClassName]: numberedListFormat === "brackets" })}>
//           <li>
//             This is our <Text strong>{LabelByPricingType[pricingType]}</Text>
//             {hasFixed ? (
//               <>
//                 {" with an additional "}
//                 <Text strong>{LabelByPricingType[PricingType.Fixed]}</Text>
//                 {" option"}
//               </>
//             ) : (
//               ""
//             )}
//             .
//           </li>
//           {numberedListFormat === "period" && <br></br>}
//           <li>
//             Our pricing must be read in conjunction with any assumptions and exclusions set out
//             below.
//           </li>
//           {numberedListFormat === "period" && <br></br>}
//           <li>If anything is unclear, please contact us immediately.</li>
//         </ol>
//       </Paragraph>
//     </>
//   );
// };

// const AssignedResourcesSection = ({
//   assignedResources,
//   currencySymbol,
//   showFeeEarners,
//   showRates,
// }: {
//   assignedResources: AssignedResource[];
//   currencySymbol: string;
//   showFeeEarners: boolean;
//   showRates: boolean;
// }) => {
//   if (!showFeeEarners) {
//     return null;
//   }
//   return (
//     <>
//       {assignedResources.length > 0 && (
//         <>
//           <Paragraph>
//             {showRates
//               ? `The following roles/individuals with the hourly rates indicated have been assigned to
//             this project:`
//               : `The following roles/individuals have been assigned to this project:`}
//           </Paragraph>
//           <Paragraph>
//             <ul className={nonStyledListClassName}>
//               {assignedResources.map((ar, index) => (
//                 <li key={index.toString()}>
//                   {ar.feeEarnerName && ar.feeEarnerName !== ""
//                     ? `${ar.feeEarnerName} (${ar.feeEarnerLevelDescription}) `
//                     : `${ar.feeEarnerLevelDescription} `}
//                   {showRates && ar.rate && ` - ${formatPrice(ar.rate, currencySymbol)}`}
//                 </li>
//               ))}
//             </ul>
//           </Paragraph>
//         </>
//       )}
//     </>
//   );
// };

const LineItemsWithTasks = ({
  lineItems,
  pricingLevel,
  currencySymbol,
  repeatTimes = 1,
  nBlankItemsLeft,
  nBlankItemsRight,
  lineBreakList,
}: {
  lineItems: LegalFeeLineItem[];
  pricingLevel: PricingLevel;
  currencySymbol: string;
  repeatTimes?: number;
  nBlankItemsLeft?: number;
  nBlankItemsRight?: number;
  lineBreakList: boolean;
}) => {
  const matterLevelPricing = PricingLevel.Matter === pricingLevel;

  return (
    <>
      {lineItems.map(({ title, tasks: tasksRaw = [], fee }, index) => {
        const tasks = tasksRaw.sort(
          (a: LegalFeeLineItemTask, b: LegalFeeLineItemTask) => a?.order - b?.order
        );
        const valuesRaw = Array(repeatTimes).fill(
          !matterLevelPricing ? formatPrice(fee, currencySymbol) : ""
        );
        const values = [
          ...(nBlankItemsLeft ? Array(nBlankItemsLeft).fill("") : []),
          ...valuesRaw,
          ...(nBlankItemsRight ? Array(nBlankItemsRight).fill("") : []),
        ];
        const lineItemData = (
          <>
            <Paragraph style={PRE_LINE_STYLE}>{title}</Paragraph>
            {tasks.length > 0 && (
              <>
                <ul>
                  {tasks.map((t: LegalFeeLineItemTask) => (
                    <>
                      <li style={PRE_LINE_STYLE} key={t.id}>
                        {t.title}
                      </li>
                      {lineBreakList && <br></br>}
                    </>
                  ))}
                </ul>
              </>
            )}
          </>
        );
        return (
          <Row
            key={index.toString()}
            label={lineItemData}
            values={values}
            style={{ verticalAlign: "top" }}
          />
        );
      })}
    </>
  );
};

const LineItemsWithoutTasks = ({
  lineItems,
  pricingLevel,
  currencySymbol,
  repeatTimes = 1,
  nBlankItemsLeft,
  nBlankItemsRight,
}: {
  lineItems: LegalFeeLineItem[];
  pricingLevel: PricingLevel;
  currencySymbol: string;
  repeatTimes?: number;
  nBlankItemsLeft?: number;
  nBlankItemsRight?: number;
}) => {
  const matterLevelPricing = PricingLevel.Matter === pricingLevel;

  return (
    <>
      {lineItems.map(({ title, fee }, index) => {
        const valuesRaw = Array(repeatTimes).fill(
          !matterLevelPricing ? formatPrice(fee, currencySymbol) : ""
        );
        const values = [
          ...(nBlankItemsLeft ? Array(nBlankItemsLeft).fill("") : []),
          ...valuesRaw,
          ...(nBlankItemsRight ? Array(nBlankItemsRight).fill("") : []),
        ];
        return (
          <Row
            key={index.toString()}
            label={title}
            values={values}
            style={{ verticalAlign: "top" }}
          />
        );
      })}
    </>
  );
};

const formatLegalFeeDiscount = ({
  legalFeeDiscount,
  currencySymbol,
}: {
  legalFeeDiscount: LegalFeeDiscount;
  currencySymbol: string;
}) => {
  if (
    isPriceDiscount(legalFeeDiscount) &&
    (legalFeeDiscount.discountRate > 0 ||
      legalFeeDiscount.discountAmount > 0 ||
      legalFeeDiscount.discountedAmount > 0)
  ) {
    return {
      rate: formatPercentage(legalFeeDiscount.discountRate),
      amount: formatPrice(legalFeeDiscount.discountAmount, currencySymbol),
      netLegalFee: formatPrice(legalFeeDiscount.discountedAmount, currencySymbol),
    };
  } else if (
    isPriceRangeDiscount(legalFeeDiscount) &&
    ((legalFeeDiscount.discountRateRange.lower !== null &&
      legalFeeDiscount.discountRateRange.lower > 0) ||
      (legalFeeDiscount.discountAmountRange.lower !== null &&
        legalFeeDiscount.discountAmountRange.lower > 0))
  ) {
    return {
      rate: formatPercentage(legalFeeDiscount.discountRateRange),
      amount: formatPrice(legalFeeDiscount.discountAmountRange, currencySymbol),
      netLegalFee: formatPrice(legalFeeDiscount.discountedAmountRange, currencySymbol),
    };
  }
};

const LineItemsBreakdownSectionSingle = ({
  onClickEdit,
  proposal,
  currencySymbol,
  taxLabel,
  includeTasksOverride,
  lineBreakList,
}: {
  onClickEdit: (matterEditorChild: string) => void;
  proposal: Pick<
    Proposal,
    | "legalFeeTotal"
    | "legalFeeTotalBeforeVisibleUplifts"
    | "legalFeeLineItems"
    | "legalFeeUplifts"
    | "legalFeeDiscount"
    | "disbursementsTotal"
    | "disbursementLineItems"
    | "disbursementsTax"
    | "legalFeeTax"
    | "grandTotal"
    | "includeTasks"
    | "includeRatesInProposalOutput"
    | "excludeTax"
    | "pricingLevel"
  >;
  currencySymbol: string;
  taxLabel: TaxLabel;
  includeTasksOverride?: boolean;
  lineBreakList: boolean;
}) => {
  const {
    legalFeeTotalBeforeVisibleUplifts,
    legalFeeLineItems,
    legalFeeUplifts,
    legalFeeDiscount,
    disbursementsTotal,
    disbursementLineItems,
    disbursementsTax,
    legalFeeTax,
    grandTotal,
    includeTasks,
    excludeTax,
    pricingLevel,
  } = proposal;

  const showTasks =
    includeTasksOverride === undefined && includeTasks ? true : includeTasksOverride;

  const formattedLegalFeeDiscount = formatLegalFeeDiscount({ legalFeeDiscount, currencySymbol });

  return (
    <>
      <table className={proposalLineItemsClassName}>
        <tbody>
          <Row
            label={{
              text: "Legal Fees",
              onClick: () => onClickEdit(MATTER_EDITOR_SCOPE),
            }}
            values={[formatPrice(legalFeeTotalBeforeVisibleUplifts, currencySymbol)]}
            strong="row"
          />
          {showTasks ? (
            <LineItemsWithTasks
              currencySymbol={currencySymbol}
              lineItems={legalFeeLineItems}
              pricingLevel={pricingLevel}
              lineBreakList={lineBreakList}
            />
          ) : (
            <LineItemsWithoutTasks
              currencySymbol={currencySymbol}
              lineItems={legalFeeLineItems}
              pricingLevel={pricingLevel}
            />
          )}
          {legalFeeUplifts.length > 0 && (
            <>
              <SpacerRow />
              <Row
                label={{
                  text: `Uplifts`,
                  onClick: () => onClickEdit(MATTER_EDITOR_UPLIFTS),
                }}
                strong="row"
              />
              {legalFeeUplifts.map(({ reason, fee, rate }, index) => (
                <Row
                  key={index.toString()}
                  label={reason + (rate ? ` (${formatPercentage(rate)})` : "")}
                  values={[formatPrice(fee, currencySymbol)]}
                />
              ))}
            </>
          )}
          {hasDiscount(legalFeeDiscount) && (
            <>
              <SpacerRow />
              <Row
                label={{
                  text: `Discount (${formattedLegalFeeDiscount?.rate})`,
                  onClick: () => onClickEdit(MATTER_EDITOR_DISCOUNTS),
                }}
                values={[
                  formatPrice(
                    isPriceDiscount(legalFeeDiscount)
                      ? legalFeeDiscount.discountAmount
                      : legalFeeDiscount.discountAmountRange,
                    currencySymbol
                  ),
                ]}
                strong="label"
              />
              <SpacerRow />
            </>
          )}

          <Row
            label={"Net Fee"}
            values={[
              formatPrice(
                isPriceDiscount(legalFeeDiscount)
                  ? legalFeeDiscount.discountedAmount
                  : legalFeeDiscount.discountedAmountRange,
                currencySymbol
              ),
            ]}
            strong="row"
          />
          <SpacerRow />
          <Row
            label={{
              text: "Disbursements",
              onClick: () => onClickEdit(MATTER_EDITOR_DISBURSEMENTS),
            }}
            values={[formatPrice(disbursementsTotal, currencySymbol)]}
            strong="row"
          />
          {disbursementLineItems.map(({ name, amount }, index) => (
            <Row
              key={index.toString()}
              label={name}
              values={[formatPrice(amount, currencySymbol)]}
            />
          ))}
          <DividerRow />
          <Row label={"Summary"} strong="label" />
          <Row
            label={`Fee subtotal${!excludeTax ? ` (excl. ${taxLabel})` : ""}`}
            values={[
              formatPrice(
                isPriceDiscount(legalFeeDiscount)
                  ? legalFeeDiscount.discountedAmount
                  : legalFeeDiscount.discountedAmountRange,
                currencySymbol
              ),
            ]}
          />
          {!excludeTax && (
            <>
              <Row
                label={`${taxLabel} ${formatPercentage(legalFeeTax.taxRate)}`}
                values={[
                  formatPrice(
                    isTax(legalFeeTax) ? legalFeeTax.taxAmount : legalFeeTax.taxAmountRange,
                    currencySymbol
                  ),
                ]}
              />
              <SpacerRow />
              <Row
                label={`Fee subtotal (including ${taxLabel})`}
                values={[
                  formatPrice(
                    isTax(legalFeeTax)
                      ? legalFeeTax.totalIncludingTax
                      : legalFeeTax.totalRangeIncludingTax,
                    currencySymbol
                  ),
                ]}
              />
            </>
          )}
          <Row
            label={`Disbursements${
              !excludeTax
                ? ` (including ${taxLabel} of ${formatPrice(
                    disbursementsTax.taxAmount,
                    currencySymbol
                  )})`
                : ""
            }`}
            values={[
              !excludeTax
                ? formatPrice(disbursementsTax.totalIncludingTax, currencySymbol)
                : formatPrice(disbursementsTotal, currencySymbol),
            ]}
          />
          <SpacerRow />
          <Row
            label={"GRAND TOTAL"}
            values={[formatPrice(grandTotal, currencySymbol)]}
            strong="row"
          />
        </tbody>
      </table>
      <Paragraph>
        <em>Note:</em> where necessary, figures are rounded to the nearest {currencySymbol}.
      </Paragraph>
    </>
  );
};

const LineItemsBreakdownSectionMultiple = ({
  onClickEdit,
  proposal,
  currencySymbol,
  taxLabel,
  includeTasksOverride,
  lineBreakList,
}: {
  onClickEdit: (matterEditorChild: string) => void;
  proposal: Pick<
    Proposal,
    | "legalFeeTotal"
    | "legalFeeTotalBeforeVisibleUplifts"
    | "legalFeeLineItems"
    | "legalFeeUplifts"
    | "legalFeeDiscount"
    | "disbursementsTotal"
    | "disbursementLineItems"
    | "disbursementsTax"
    | "legalFeeTax"
    | "grandTotal"
    | "includeTasks"
    | "excludeTax"
    | "fixedLegalFeeTotal"
    | "fixedLegalFeeTax"
    | "fixedLegalFeeDiscount"
    | "fixedGrandTotal"
    | "pricingType"
    | "pricingLevel"
  >;
  currencySymbol: string;
  taxLabel: TaxLabel;
  includeTasksOverride?: boolean;
  lineBreakList: boolean;
}) => {
  const {
    legalFeeTotalBeforeVisibleUplifts,
    legalFeeLineItems,
    legalFeeUplifts,
    legalFeeDiscount,
    disbursementsTotal,
    disbursementLineItems,
    disbursementsTax,
    legalFeeTax,
    grandTotal,
    includeTasks,
    excludeTax,
    fixedLegalFeeTotal,
    fixedLegalFeeTax,
    fixedLegalFeeDiscount,
    fixedGrandTotal,
    pricingType,
    pricingLevel,
  } = proposal;

  const showTasks =
    includeTasksOverride === undefined && includeTasks ? true : includeTasksOverride;

  const formattedLegalFeeDiscount = formatLegalFeeDiscount({ legalFeeDiscount, currencySymbol });

  return (
    <>
      <table className={proposalLineItemsClassName}>
        <thead>
          <Row
            label=""
            values={[
              <Text strong style={{ textTransform: "capitalize" }} key="rangeHeader">
                {LabelByPricingType[pricingType]}
              </Text>,
              <Text strong style={{ textTransform: "capitalize" }} key="fixedHeader">
                {LabelByPricingType[PricingType.Fixed]}
              </Text>,
            ]}
          />
        </thead>
        <tbody>
          <Row
            label={{
              text: "Legal Fees",
              onClick: () => onClickEdit(MATTER_EDITOR_SCOPE),
            }}
            values={[
              <Text key="rangeHeaderValue">
                {formatPrice(legalFeeTotalBeforeVisibleUplifts, currencySymbol)}
              </Text>,
              <Text key="fixedHeaderValue">{formatPrice(fixedLegalFeeTotal, currencySymbol)}</Text>,
            ]}
          />
          {showTasks ? (
            <LineItemsWithTasks
              currencySymbol={currencySymbol}
              lineItems={legalFeeLineItems}
              nBlankItemsRight={1}
              pricingLevel={pricingLevel}
              lineBreakList={lineBreakList}
            />
          ) : (
            <LineItemsWithoutTasks
              currencySymbol={currencySymbol}
              lineItems={legalFeeLineItems}
              nBlankItemsRight={1}
              pricingLevel={pricingLevel}
            />
          )}
          {legalFeeUplifts.length > 0 && (
            <>
              <SpacerRow />
              <Row
                label={{
                  text: `Uplifts`,
                  onClick: () => onClickEdit(MATTER_EDITOR_UPLIFTS),
                }}
                strong="row"
              />
              {legalFeeUplifts.map(({ reason, fee, rate }, index) => (
                <Row
                  key={index.toString()}
                  label={reason + (rate ? ` (${formatPercentage(rate)})` : "")}
                  values={[formatPrice(fee, currencySymbol), ""]}
                />
              ))}
            </>
          )}
          {hasDiscount(legalFeeDiscount) && (
            <>
              <SpacerRow />
              <Row
                label={{
                  text: `Discount (${formattedLegalFeeDiscount?.rate})`,
                  onClick: () => onClickEdit(MATTER_EDITOR_DISCOUNTS),
                }}
                values={[
                  formatPrice(
                    isPriceDiscount(legalFeeDiscount)
                      ? legalFeeDiscount.discountAmount
                      : legalFeeDiscount.discountAmountRange,
                    currencySymbol
                  ),
                  fixedLegalFeeDiscount.discountAmount > 0
                    ? formatPrice(fixedLegalFeeDiscount.discountAmount, currencySymbol)
                    : "",
                ]}
                strong="label"
              />
              <SpacerRow />
            </>
          )}

          <Row
            label={"Net Fee"}
            values={[
              formatPrice(
                isPriceDiscount(legalFeeDiscount)
                  ? legalFeeDiscount.discountedAmount
                  : legalFeeDiscount.discountedAmountRange,
                currencySymbol
              ),
              formatPrice(fixedLegalFeeDiscount.discountedAmount, currencySymbol),
            ]}
            strong="row"
          />
          <SpacerRow />
          <Row
            label={{
              text: "Disbursements",
              onClick: () => onClickEdit(MATTER_EDITOR_DISBURSEMENTS),
            }}
            values={[
              formatPrice(disbursementsTotal, currencySymbol),
              formatPrice(disbursementsTotal, currencySymbol),
            ]}
            strong="row"
          />
          {disbursementLineItems.map(({ name, amount }, index) => (
            <Row
              key={index.toString()}
              label={name}
              values={[formatPrice(amount, currencySymbol), formatPrice(amount, currencySymbol)]}
            />
          ))}
          <DividerRow colSpan={4} />
          <Row label={"Summary"} strong="label" />
          <Row
            label={`Fee subtotal${!excludeTax ? ` (excl. ${taxLabel})` : ""}`}
            values={[
              formatPrice(
                isPriceDiscount(legalFeeDiscount)
                  ? legalFeeDiscount.discountedAmount
                  : legalFeeDiscount.discountedAmountRange,
                currencySymbol
              ),
              formatPrice(fixedLegalFeeDiscount.discountedAmount, currencySymbol),
            ]}
          />
          {!excludeTax && (
            <>
              <Row
                label={`${taxLabel} ${formatPercentage(legalFeeTax.taxRate)}`}
                values={[
                  formatPrice(
                    isTax(legalFeeTax) ? legalFeeTax.taxAmount : legalFeeTax.taxAmountRange,
                    currencySymbol
                  ),
                  formatPrice(fixedLegalFeeTax.taxAmount, currencySymbol),
                ]}
              />
              <SpacerRow />
              <Row
                label={`Fee subtotal (including ${taxLabel})`}
                values={[
                  formatPrice(
                    isTax(legalFeeTax)
                      ? legalFeeTax.totalIncludingTax
                      : legalFeeTax.totalRangeIncludingTax,
                    currencySymbol
                  ),
                  formatPrice(fixedLegalFeeTax.totalIncludingTax, currencySymbol),
                ]}
              />
            </>
          )}
          <Row
            label={`Disbursements${
              !excludeTax
                ? ` (including ${taxLabel} of ${formatPrice(
                    disbursementsTax.taxAmount,
                    currencySymbol
                  )})`
                : ""
            }`}
            values={[
              !excludeTax
                ? formatPrice(disbursementsTax.totalIncludingTax, currencySymbol)
                : formatPrice(disbursementsTotal, currencySymbol),
              !excludeTax
                ? formatPrice(disbursementsTax.totalIncludingTax, currencySymbol)
                : formatPrice(disbursementsTotal, currencySymbol),
            ]}
          />
          <SpacerRow />
          <Row
            label={"GRAND TOTAL"}
            values={[
              formatPrice(grandTotal, currencySymbol),
              formatPrice(fixedGrandTotal, currencySymbol),
            ]}
            strong="row"
          />
        </tbody>
      </table>
      <Paragraph>
        <em>Note:</em> where necessary, figures are rounded to the nearest {currencySymbol}.
      </Paragraph>
    </>
  );
};

export const LineItemsBreakdownSection = ({
  onClickEdit,
  proposal,
  currencySymbol,
  taxLabel,
  includeTasksOverride,
  numberedListFormat,
}: {
  onClickEdit: (matterEditorChild: string) => void;
  proposal: Pick<
    Proposal,
    | "legalFeeTotal"
    | "legalFeeTotalBeforeVisibleUplifts"
    | "legalFeeLineItems"
    | "legalFeeUplifts"
    | "legalFeeDiscount"
    | "disbursementsTotal"
    | "disbursementLineItems"
    | "disbursementsTax"
    | "legalFeeTax"
    | "grandTotal"
    | "includeTasks"
    | "excludeTax"
    | "fixedLegalFeeTotal"
    | "fixedLegalFeeTax"
    | "fixedLegalFeeDiscount"
    | "includeRatesInProposalOutput"
    | "fixedGrandTotal"
    | "pricingType"
    | "pricingLevel"
  >;
  currencySymbol: string;
  taxLabel: TaxLabel;
  numberedListFormat: NumberedListFormat;
  includeTasksOverride?: boolean;
}) => {
  const { fixedLegalFeeTotal, pricingType } = proposal;

  const lineBreakList = numberedListFormat === "period";

  if (fixedLegalFeeTotal && fixedLegalFeeTotal.amount !== 0 && pricingType !== PricingType.Fixed) {
    return (
      <LineItemsBreakdownSectionMultiple
        includeTasksOverride={includeTasksOverride}
        currencySymbol={currencySymbol}
        taxLabel={taxLabel}
        onClickEdit={onClickEdit}
        proposal={proposal}
        lineBreakList={lineBreakList}
      />
    );
  }

  return (
    <LineItemsBreakdownSectionSingle
      includeTasksOverride={includeTasksOverride}
      currencySymbol={currencySymbol}
      taxLabel={taxLabel}
      onClickEdit={onClickEdit}
      proposal={proposal}
      lineBreakList={lineBreakList}
    />
  );
};

// const AssumptionsAndExclusionsSection = ({
//   onClickEdit,
//   assumptions,
//   exclusions,
//   numberedListFormat,
// }: {
//   onClickEdit: (matterEditorChild: string) => void;
//   assumptions: string[];
//   exclusions: string[];
//   numberedListFormat: NumberedListFormat;
// }) => {
//   const useBrackets = numberedListFormat === "brackets";
//   return (
//     <>
//       <Paragraph>
//         In this proposal, we have made a number of specific assumptions and for clarity we have
//         excluded a number of matters from the scope of work. These assumptions and exclusions are
//         very important as our pricing is based on them. Please note:
//       </Paragraph>
//       <Paragraph>
//         <ol className={classNames({ [bracketsListClassName]: useBrackets })}>
//           <li>
//             If the assumptions and exclusions listed below are not correct, you must let us know
//             immediately.
//           </li>
//           <li>
//             If the work requires changes so that the assumptions and exclusions are not longer
//             correct, our pricing is likely to be affected but we will notify you in a timely manner
//             and discuss your options with you.
//           </li>
//         </ol>
//       </Paragraph>
//       <Paragraph>
//         <Text strong>ASSUMPTIONS</Text>
//         <EditButton onClick={() => onClickEdit(MATTER_EDITOR_ASSUMPTIONS)} />
//         <ol
//           className={classNames(lowerAlphaListClassName, {
//             [bracketsListClassName]: useBrackets,
//           })}
//         >
//           {assumptions.map((a, index) => (
//             <>
//               <li style={PRE_LINE_STYLE} key={index.toString()}>
//                 {a}
//               </li>
//               {!useBrackets && <br></br>}
//             </>
//           ))}
//         </ol>
//       </Paragraph>
//       <Paragraph>
//         <Text strong>EXCLUSIONS</Text>
//         <EditButton onClick={() => onClickEdit(MATTER_EDITOR_EXCLUSIONS)} />
//         <ol
//           className={classNames(lowerAlphaListClassName, {
//             [bracketsListClassName]: useBrackets,
//           })}
//         >
//           {exclusions.map((e, index) => (
//             <>
//               <li style={PRE_LINE_STYLE} key={index.toString()}>
//                 {e}
//               </li>
//               {!useBrackets && <br></br>}
//             </>
//           ))}
//         </ol>
//       </Paragraph>
//     </>
//   );
// };

// const FeeRiskProfileSection = ({
//   totalWidth = 800,
//   currencySymbol,
//   lowestAnticipatedFee,
//   highestAnticipatedFee,
//   lowestFee,
//   highestFee,
//   fixedFee,
//   cappedFee,
//   showCappedFee,
// }: FeeRiskProfileDiagramProps & { showCappedFee: boolean }) => {
//   const cappedFeeInclude = showCappedFee && cappedFee !== undefined;
//   const lowestAnticipatedFeeString = formatPrice(lowestAnticipatedFee, currencySymbol);
//   const highestAnticipatedFeeString = formatPrice(highestAnticipatedFee, currencySymbol);
//   const highestFeeString = formatPrice(highestFee, currencySymbol);
//   const fixedFeeString = formatPrice(fixedFee, currencySymbol);
//   let cappedFeeString: string;
//   let cappedFeeMinusFixedFeeString: string;
//   if (cappedFee) {
//     cappedFeeString = formatPrice(cappedFee, currencySymbol);
//     cappedFeeMinusFixedFeeString = formatPrice(cappedFee - fixedFee, currencySymbol);
//   } else {
//     cappedFeeString = "";
//     cappedFeeMinusFixedFeeString = "";
//   }

//   return (
//     <Paragraph>
//       <Text strong>FEE RISK PROFILE</Text>
//       <div className={centerContainerClassName}>
//         <FeeRiskProfileDiagram
//           totalWidth={totalWidth}
//           currencySymbol={currencySymbol}
//           lowestAnticipatedFee={lowestAnticipatedFee ?? undefined}
//           highestAnticipatedFee={highestAnticipatedFee ?? undefined}
//           lowestFee={lowestFee ?? undefined}
//           highestFee={highestFee ?? undefined}
//           fixedFee={fixedFee ?? undefined}
//           cappedFee={showCappedFee ? cappedFee ?? undefined : undefined}
//         />
//       </div>
//       <table className={feeRiskTableClassName}>
//         <thead>
//           <tr>
//             <td>{textContent.feeRiskTableColumnTitlesOne.text()}</td>
//             <td>
//               <Text>{textContent.feeRiskTableColumnTitlesTwo.text()}</Text>
//             </td>
//             <td>
//               <Text>{textContent.feeRiskTableColumnTitlesThree.text()}</Text>
//             </td>
//           </tr>
//         </thead>
//         <tbody>
//           <tr>
//             <td>
//               {cappedFeeInclude
//                 ? textContent.feeRiskTableHourlyRatesWithCap.text()
//                 : textContent.feeRiskTableHourlyRatesWithoutCap.text()}
//             </td>
//             <td>
//               <Text>
//                 {cappedFeeInclude
//                   ? textContent.feeRiskTableTextOneWithCap.text(
//                       lowestAnticipatedFeeString,
//                       highestAnticipatedFeeString,
//                       cappedFeeString
//                     )
//                   : textContent.feeRiskTableTextOneWithoutCap.text(
//                       lowestAnticipatedFeeString,
//                       highestAnticipatedFeeString
//                     )}
//               </Text>
//             </td>
//             <td>
//               <Text>
//                 {cappedFeeInclude
//                   ? textContent.feeRiskTableTextTwoWithCap.text(
//                       cappedFeeString,
//                       cappedFeeMinusFixedFeeString
//                     )
//                   : textContent.feeRiskTableTextTwoWithoutCap.text(highestFeeString)}
//               </Text>
//             </td>
//           </tr>
//           <tr>
//             <td>{textContent.feeRiskTableFixedFee.text()}</td>
//             <td>{textContent.feeRiskTableTextThree.text(fixedFeeString)}</td>
//             <td>{textContent.feeRiskTableTextFour.text(fixedFeeString)}</td>
//           </tr>
//         </tbody>
//       </table>
//     </Paragraph>
//   );
// };

export const ProposalExportPreviewModalBody = ({
  logo,
  footer,
  proposal,
  legalFees,
  onClickEdit,
  currencySymbol,
  taxLabel,
  showFeeEarners,
  // showRates,
  // includeTasksOverride,
  numberedListFormat,
  lineBreakInLists,
  feeRiskValid,
}: {
  logo: string;
  footer: string;
  proposal: Proposal;
  legalFees?: number | { lower: number; upper: number };
  onClickEdit: (matterEditorChild: string) => void;
  currencySymbol: string;
  taxLabel: TaxLabel;
  showFeeEarners: boolean;
  showRates: boolean;
  includeTasksOverride?: boolean;
  numberedListFormat: NumberedListFormat;
  lineBreakInLists: boolean;
  feeRiskValid?: boolean;
}) => {
  // const { pricingType, assignedResources, assumptions, exclusions, fixedLegalFeeTotal } = proposal;

  const [feeRiskSvgString, setFeeRiskSvgString] = useState<string>("");

  const vpdProposal = {
    ...proposal,
    pricingType: proposal.pricingType as any,
    pricingLevel: proposal.pricingLevel as any,
    includeFeeEarnersInProposalOutput: showFeeEarners,
  };

  const formatOptions = {
    numberedListFormat: numberedListFormat,
    lineBreakInLists: lineBreakInLists,
  };

  const contentGenerator = new JSXContentGenerator();
  const proposalContentBuilder = new ProposalContentBuilder(
    vpdProposal,
    formatOptions,
    currencySymbol as any,
    taxLabel,
    legalFees ?? 0,
    contentGenerator
  );

  useEffect(() => {
    const fetchSvgString = async () => {
      if (
        proposal.feeRiskInclude &&
        legalFees &&
        !isNumber(legalFees) &&
        legalFees.lower &&
        legalFees.upper &&
        proposal.priceOptions.feeRiskLower &&
        proposal.priceOptions.feeRiskUpper &&
        proposal.priceOptions.fixedChosen &&
        feeRiskValid
      ) {
        const result = (await renderFeeRiskProfileDiagram({
          totalWidth: 600,
          currencySymbol,
          lowestAnticipatedFee: legalFees.lower,
          highestAnticipatedFee: legalFees.upper,
          lowestFee: proposal.priceOptions.feeRiskLower,
          highestFee: proposal.priceOptions.feeRiskUpper,
          fixedFee: proposal.priceOptions.fixedChosen,
          cappedFee: proposal.priceOptions.cappedChosen ?? undefined,
        })) as string;
        setFeeRiskSvgString(result);
      }
    };
    fetchSvgString();
  }, [proposal.feeRiskInclude]);

  const editButton = (tabName: string) => {
    return <EditButton onClick={() => onClickEdit(tabName)} />;
  };

  const editButtons: Record<string, JSX.Element> = {};
  editButtons[textContent.legalFeesTitle.text()] = editButton(MATTER_EDITOR_SCOPE);
  editButtons[textContent.upliftsTitle.text()] = editButton(MATTER_EDITOR_UPLIFTS);
  editButtons[textContent.discountTitle.text()] = editButton(MATTER_EDITOR_DISCOUNTS);
  editButtons[textContent.disbursementsTitle.text()] = editButton(MATTER_EDITOR_DISBURSEMENTS);
  editButtons[textContent.assumptionsTitle.text()] = editButton(MATTER_EDITOR_ASSUMPTIONS);
  editButtons[textContent.exclusionsTitle.text()] = editButton(MATTER_EDITOR_EXCLUSIONS);
  editButtons[textContent.feeRiskProfileTitle.text()] = editButton(MATTER_EDITOR_FEE_RISK_PROFILE);

  return (
    <div>
      <HeaderLogoSection logo={logo} />
      {proposalContentBuilder.buildProposal({ feeRisk: feeRiskSvgString }, editButtons)}
      {/* <Paragraph>Client Name: {proposal.clientName}</Paragraph>
      <Paragraph>Matter Title: {proposal.matterTitle}</Paragraph>
      <PricingTypeAndIntroSection
        numberedListFormat={numberedListFormat}
        pricingType={pricingType}
        hasFixed={
          fixedLegalFeeTotal && fixedLegalFeeTotal.amount !== 0 && pricingType !== PricingType.Fixed
        }
      />
      <AssignedResourcesSection
        currencySymbol={currencySymbol}
        assignedResources={assignedResources}
        showFeeEarners={showFeeEarners}
        showRates={showRates}
      />
      <LineItemsBreakdownSection
        includeTasksOverride={includeTasksOverride}
        currencySymbol={currencySymbol}
        taxLabel={taxLabel}
        onClickEdit={onClickEdit}
        proposal={proposal}
        numberedListFormat={numberedListFormat}
      />
      <AssumptionsAndExclusionsSection
        numberedListFormat={numberedListFormat}
        onClickEdit={onClickEdit}
        assumptions={assumptions}
        exclusions={exclusions}
      />
      {proposal.feeRiskInclude &&
      legalFeesForFeeRisk.lower &&
      legalFeesForFeeRisk.upper &&
      proposal.priceOptions.feeRiskLower &&
      proposal.priceOptions.feeRiskUpper &&
      proposal.priceOptions.fixedChosen &&
      feeRiskValid ? (
        <FeeRiskProfileSection
          totalWidth={600}
          currencySymbol={currencySymbol}
          lowestAnticipatedFee={legalFeesForFeeRisk?.lower}
          highestAnticipatedFee={legalFeesForFeeRisk?.upper}
          lowestFee={proposal.priceOptions.feeRiskLower}
          highestFee={proposal.priceOptions.feeRiskUpper}
          fixedFee={proposal.priceOptions.fixedChosen}
          cappedFee={proposal.priceOptions.cappedChosen ?? undefined}
          showCappedFee={proposal.feeRiskCappedFeeInclude}
        />
      ) : null} */}
      <FooterSection footer={footer} />
    </div>
  );
};
